import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './dialog';
import { Button } from './button';
import { FileText, Download, ExternalLink, X, Eye, Image as ImageIcon, File } from 'lucide-react';

interface DocumentViewerProps {
  documentUrl: string;
  documentFilename?: string;
  trigger?: React.ReactNode;
  className?: string;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  documentUrl,
  documentFilename = 'Document',
  trigger,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Determine file type from URL
  const getFileType = (url: string): 'pdf' | 'image' | 'unknown' => {
    const ext = url.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return 'pdf';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext || '')) return 'image';
    return 'unknown';
  };

  const fileType = getFileType(documentUrl);

  // Get the full URL for the document
  const getFullUrl = (url: string) => {
    if (url.startsWith('http')) return url;
    // Construct full URL based on API base
    return url;
  };

  const fullUrl = getFullUrl(documentUrl);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = fullUrl;
    link.download = documentFilename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenInNewTab = () => {
    window.open(fullUrl, '_blank');
  };

  const getFileIcon = () => {
    switch (fileType) {
      case 'pdf':
        return <FileText className="h-5 w-5" />;
      case 'image':
        return <ImageIcon className="h-5 w-5" />;
      default:
        return <File className="h-5 w-5" />;
    }
  };

  return (
    <>
      {/* Trigger */}
      {trigger ? (
        <div onClick={() => setIsOpen(true)} className={`cursor-pointer ${className}`}>
          {trigger}
        </div>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsOpen(true)}
          className={`gap-2 ${className}`}
        >
          <Eye className="h-4 w-4" />
          View Document
        </Button>
      )}

      {/* Document Preview Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {getFileIcon()}
              <span className="truncate">{documentFilename}</span>
            </DialogTitle>
            <DialogDescription>
              Supporting document for leave request
            </DialogDescription>
          </DialogHeader>

          {/* Document Content */}
          <div className="flex-1 overflow-auto min-h-[400px] bg-muted rounded-lg">
            {fileType === 'pdf' ? (
              <iframe
                src={fullUrl}
                className="w-full h-full min-h-[500px] rounded-lg"
                title={documentFilename}
              />
            ) : fileType === 'image' ? (
              <div className="flex items-center justify-center h-full p-4">
                {!imageError ? (
                  <img
                    src={fullUrl}
                    alt={documentFilename}
                    className="max-w-full max-h-[60vh] object-contain rounded-lg shadow-lg"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div className="text-center text-muted-foreground">
                    <ImageIcon className="h-16 w-16 mx-auto mb-4 opacity-50" />
                    <p>Unable to load image</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleOpenInNewTab}
                      className="mt-4"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Open in New Tab
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                <File className="h-16 w-16 text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">
                  This file type cannot be previewed directly.
                </p>
                <Button onClick={handleDownload} className="gap-2">
                  <Download className="h-4 w-4" />
                  Download File
                </Button>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={handleOpenInNewTab} className="gap-2">
              <ExternalLink className="h-4 w-4" />
              Open in New Tab
            </Button>
            <Button onClick={handleDownload} className="gap-2">
              <Download className="h-4 w-4" />
              Download
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DocumentViewer;
